import { type ChangeEvent, useEffect, useState } from 'react';

import { useAtom, useAtomValue } from 'jotai';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { deepSet } from '@core/Utils/DeepSet';

import { hasFinCalcDataLoadedAtom } from '../../Atoms/FinCalcData.atom';
import { finCalcDataFieldValueAtom } from '../../Atoms/FinCalcDataFieldValue.atom';
import type { CheckboxField } from '../../Entities/FinCalcFields.entity';
import { useSetValidity } from '../../Hooks/useSetValidity';

import styles from './CheckboxButton.module.css';

export const CheckboxButtonElement = ({
  id,
  label,
  name,
  errorMessage,
  isDisabled,
  isValid,
  checkboxOptions,
  dataTestId,
}: CheckboxField) => {
  const hasFinCalcDataLoaded = useAtomValue(hasFinCalcDataLoadedAtom);
  const [value, setValue] = useAtom(finCalcDataFieldValueAtom(id));
  const [hasTouched, setHasTouched] = useState(false);
  const ref = useSetValidity({ hasTouched, isValid, errorMessage });
  const isDefaultChecked = ref?.current?.checked;

  const handleBlur = () => {
    setHasTouched(true);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(deepSet(value ?? {}, e.target.id, e.target.checked));
  };

  useEffect(() => {
    if (!hasFinCalcDataLoaded) return;

    for (const option of checkboxOptions ?? []) {
      if (option.defaultValue && value?.[option.id] === undefined) {
        setValue(deepSet(value ?? {}, option.id, !!option.defaultValue));
      }
    }
  }, [hasFinCalcDataLoaded]);

  return (
    <div className={styles.wrapper}>
      {checkboxOptions?.map(option => (
        <div className={styles.labelWrapper} key={option.additionalServiceKey}>
          <label htmlFor={id}>
            <input
              data-test-id={dataTestId}
              ref={ref}
              type="checkbox"
              name={name || undefined}
              id={option.additionalServiceKey}
              checked={!!value?.[option.additionalServiceKey]}
              disabled={isDisabled}
              onChange={handleChange}
              onBlur={handleBlur}
            />
            <span className={`${hasTouched && !isDefaultChecked && !isValid && styles.error}`} />
            {label && option.label}
          </label>
          {option.icon && option.icon}
        </div>
      ))}
      {hasTouched && !isDefaultChecked && !isValid && <small>{errorMessage}</small>}
    </div>
  );
};
